/*
 * Arib Subtitle Track Controller
 *
 * 字幕用m3u8の取得および更新処理の実装。
 *
 * master.m3u8に字幕用m3u8が含まれてない場合に、
 * 連携するのに利用する。
 *
 * hls.jsのTimelineController, SubtitleTrackController,
 * PlaylistLoaderを参考にした.
 *
*/
import Event from 'hls.js/lib/events';
import EventHandler from 'hls.js/lib/event-handler';
import {logger} from 'hls.js/lib/utils/logger';
import DrcsDecoder from './drcs-decoder.js';

const REG_VTT_TAG_EDGE = /\<c([^>]*)>([^<]*)<\/c>/g;
const REG_VTT_CLASS_EDGE = /\.([^\.]+)/g;

function clearCurrentCues(track) {
  if (track && track.cues) {
    while (track.cues.length > 0) {
      track.removeCue(track.cues[0]);
    }
  }
  const trackContainer = document.getElementById('text-track-container');
  if (trackContainer === undefined) return;
  trackContainer.innerHTML = '';
  const debugContainer = document.getElementById('subtitle-debug-view');
  if (debugContainer === undefined) return;
  debugContainer.innerHTML = '';
  const drcsDebugContainer = document.getElementById('drcs');
  if (drcsDebugContainer === undefined) return;
  drcsDebugContainer.innerHTML = '';
}

function reuseVttTextTrack(inUseTrack, manifestTrack) {
  return inUseTrack && inUseTrack.label === manifestTrack.name && !(inUseTrack.textTrack1 || inUseTrack.textTrack2);
}

function filterSubtitleTracks(textTrackList) {
  let tracks = [];

  for (let i = 0; i < textTrackList.length; i++) {

    if (textTrackList[i].kind === 'subtitles') {
      tracks.push(textTrackList[i]);
    }
  }
  return tracks;
}

function showSubtitle(isShow) {
  const trackContainer = document.getElementById('text-track-container');
  if (trackContainer === undefined) return;
  trackContainer.style.display = isShow? 'inline-block' : 'none';
}

function isWindows() {
  return navigator.platform.indexOf("Win") !== -1;
}
class AribSubtitleTrackController extends EventHandler {

  // subtitle-track-controller.js
  constructor(hls) {
    super(hls,
               Event.MEDIA_ATTACHING,
               Event.MEDIA_ATTACHED,
               Event.MEDIA_DETACHING,
               Event.MANIFEST_LOADING,
               Event.MANIFEST_LOADED,
               Event.SUBTITLE_TRACK_LOADING,
               Event.SUBTITLE_TRACK_LOADED);
    this.tracks = [];
    this.trackId = -1;
    this.media = hls.config.media;
    this.subtitleDisplay = false;
    this.loaders = {};
    this.manifestUrl = undefined;
    this.showMode = 'showing'; // showing, hidden, outside
    this.useNativeRendering = this.hls.config.useNativeRendering|false;


    if (hls.config.initPDT !== undefined) {
      setTimeout(() => {
        this.onManifestLoading();
        this.onManifestLoaded({});
      }, 0);
    }
    //hls.config.enableWebVTT = false;
  }

  // subtitle-track-controller.js
  _onTextTracksChanged() {
    // Media is undefined when switching streams via loadSource()
    if (!this.media) {
      return;
    }
    let trackId = -1;
    let tracks = filterSubtitleTracks(this.media.textTracks);
    for (let id = 0; id < tracks.length; id++) {
      if (tracks[id].showMode === undefined) tracks[id].showMode = 'showing';
      if (this.useNativeRendering) {
        if (tracks[id].showMode !== 'hidden') {
          trackId = id;
          this.showMode = tracks[id].showMode;
          tracks[id].mode = 'showing';
        } else {
          tracks[id].mode = 'hidden';
        }

      } else {
        if (tracks[id].showMode !== 'hidden') {
          console.log('showMode', tracks[id].showMode);
          trackId = id;
          this.showMode = tracks[id].showMode;
          showSubtitle(this.showMode === 'showing');
        } else {
          //clearCurrentCues(tracks[id]);
          showSubtitle(false);
        }
      }
    }

    // Setting current subtitleTrack will invoke code.
    this.subtitleTrack = trackId;
  }

  _cleanTracks() {
    // clear outdated subtitles
    const media = this.media;
    if (media) {
      const textTracks = media.textTracks;
      if (textTracks) {
        for (let i = 0; i < textTracks.length; i++) {
          clearCurrentCues(textTracks[i]);
        }
      }
    }
  }

  // subtitle-track-controller.js
  destroy() {
    this.loaders = {};
    EventHandler.prototype.destroy.call(this);
    this.tracks = [];
    this.trackId = -1;
    this.media = undefined;

  }

  onMediaAttaching(data) {
    this.media = data.media;
    this._cleanTracks();
    console.log('onMediaAttaching');
  }

  // subtitle-track-controller.js
  // Listen for subtitle track change, then extract the current track ID.
  onMediaAttached(data) {
    this.media = data.media;
    if (!this.media) {
      return;
    }

    if (this.queuedDefaultTrack !== undefined) {
      this.subtitleTrack = this.queuedDefaultTrack;
      delete this.queuedDefaultTrack;
    }

    this.trackChangeListener = this._onTextTracksChanged.bind(this);

    this.useTextTrackPolling = !(this.media.textTracks && 'onchange' in this.media.textTracks);
    if (this.useTextTrackPolling) {
      this.subtitlePollingInterval = setInterval(() => {
        this.trackChangeListener();
      }, 500);
    } else {
      this.media.textTracks.addEventListener('change', this.trackChangeListener);
    }
    console.log('onMediaAttached');
  }

  // subtitle-track-controller.js
  onMediaDetaching() {
    if (!this.media) {
      return;
    }
    if (this.useTextTrackPolling) {
      clearInterval(this.subtitlePollingInterval);
    } else {
      this.media.textTracks.removeEventListener('change', this.trackChangeListener);
    }

    this.media = undefined;
  }

  // subtitle-track-controller.js
  // Reset subtitle tracks on manifest loading
  onManifestLoading() {
    this.tracks = [];
    this.trackId = -1;
  }

  // timeline-controller, subtitle-track-controller
  // hlsのMANIFEST_LOADEDイベントをハンドリング
  onManifestLoaded(data) {
    console.log('onManifestLoaded');
    // timeline-controller
    this.textTracks = [];
    this.unparsedVttFrags = this.unparsedVttFrags || [];
    this.initPTS = undefined;
    this.cueRanges = [];
    this.manifestUrl = data.url;

    // subtitle-track-controller
    this.trackId = -1;
    this.tracks = this.createSubtitlePlaylistTrack(); //force
    //console.dir(this.tracks);

    // timeline-controller, subtitle-track-controller
    //if (this.config.enableWebVTT) {
      const inUseTracks = this.media ? this.media.textTracks : [];

      this.tracks.forEach((track, index) => {
        // timeline-controller
        let textTrack;
        if (index < inUseTracks.length) {
          const inUseTrack = inUseTracks[index];
          // Reuse tracks with the same label, but do not reuse 608/708 tracks
          if (reuseVttTextTrack(inUseTrack, track)) {
            textTrack = inUseTrack;
          }
        }
        if (!textTrack) {
            //textTrack = this.createTextTrack('subtitles', track.name, track.lang);
            textTrack = this.createTextTrack('subtitles', track.name, track.lang);
        }
        this.setupTextTrack(textTrack);
        textTrack.mode = this.useNativeRendering && track.default ? 'showing' : 'hidden';
        this.textTracks.push(textTrack);

        // subtitle-track-controller
        if (track.default) {
          // setting this.subtitleTrack will trigger internal logic
          // if media has not been attached yet, it will fail
          // we keep a reference to the default track id
          // and we'll set subtitleTrack when onMediaAttached is triggered
          if (this.media) {
            this.subtitleTrack = track.id;
          } else {
            this.queuedDefaultTrack = track.id;
          }
        }

      });
    //}

    this.hls.trigger(Event.SUBTITLE_TRACKS_UPDATED, {subtitleTracks : this.tracks});
  }

  // subtitle-track-controller.js
  // Trigger subtitle track playlist reload.
  onTick() {
    const trackId = this.trackId;
    const subtitleTrack = this.tracks[trackId];
    if (!subtitleTrack) {
      return;
    }

    const details = subtitleTrack.details;
    //check if we need to load playlist for this subtitle Track
    if (details === undefined || /*details.live === true*/ this.isLive()) {
      // track not retrieved yet, or live playlist we need to (re)load it
      logger.log(`(re)loading playlist for subtitle track ${trackId}`);
      const url = subtitleTrack.url +'?t=' + new Date().getTime();
      this.hls.trigger(Event.SUBTITLE_TRACK_LOADING, {url: url, id: trackId});
      // playlist-loader#onSubtitleTrackLoading
    }
  }

  onSubtitleTrackLoaded(data) {
    console.log('onSubtitleTrackLoaded');
    if (data.id < this.tracks.length) {
      logger.log(`subtitle track ${data.id} loaded`);
      this.tracks[data.id].details = data.details;
      // check if current playlist is a live playlist
      if (!this.hls.config.useStub && data.details.live && !this.timer) {
        // if live playlist we will have to reload it periodically
        // set reload period to playlist target duration
        this.timer = setInterval(() => {
          this.onTick();
        }, 1000 * data.details.targetduration, this);
      }
      if ((this.hls.config.useStub || !data.details.live) && this.timer) {
        // playlist is not live and timer is armed : stopping it
        clearInterval(this.timer);
        this.timer = null;
      }
    }
  }

  /** get alternate subtitle tracks list from playlist **/
  get subtitleTracks() {
    return this.tracks;
  }

  /** get index of the selected subtitle track (index in subtitle track lists) **/
  get subtitleTrack() {
   return this.trackId;
  }

  /** select a subtitle track, based on its index in subtitle track lists**/
  set subtitleTrack(subtitleTrackId) {
    if (this.trackId !== subtitleTrackId) {// || this.tracks[subtitleTrackId].details === undefined) {
      this.setSubtitleTrackInternal(subtitleTrackId);
    }
  }

  setSubtitleTrackInternal(newId) {
    // check if level idx is valid
    if (newId < -1 || newId >= this.tracks.length) {
      return;
    }

    // stopping live reloading timer if any
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }

    let textTracks = filterSubtitleTracks(this.media.textTracks);

    // hide currently enabled subtitle track
    if (this.trackId !== -1 && this.subtitleDisplay) {
      textTracks[this.trackId].mode = 'hidden';
    }

    this.trackId = newId;
    logger.log(`switching to subtitle track ${newId}`);
    this.hls.trigger(Event.SUBTITLE_TRACK_SWITCH, {id: newId});

    if (newId === -1) {
      return;
    }

    let subtitleTrack = this.tracks[newId];
    if (this.subtitleDisplay) {
      textTracks[newId].mode = 'showing';
    }

    // check if we need to load playlist for this subtitle Track
    let details = subtitleTrack.details;
    if (details === undefined || details.live === true) {
      // track not retrieved yet, or live playlist we need to (re)load it
      logger.log(`(re)loading playlist for subtitle track ${newId}`);
      const url = subtitleTrack.url +'?t=' + new Date().getTime();
      this.hls.trigger(Event.SUBTITLE_TRACK_LOADING, {url: url, id: newId});
      console.log('onSubtitleTrackLoading', subtitleTrack.url, newId);
    }
  }

  // 字幕用のPlaylist Trackを設定
  createSubtitlePlaylistTrack() {
    let tracks = [];
    const track = {};
    track.groupId =  'subs';
    track.name = '日本語';
    track.type = 'subtitle';
    track.default = 'YES';
    track.autoselect = 'YES';
    track.forced = 'YES';
    track.url = this.createSubtitlePlaylistUrl(this.manifestUrl);
    track.lang = 'ja';
    track.id = 0;
    tracks.push(track);
    return tracks;
  }

  isLive() {
    if (!this.manifestUrl) {
      return false;
    }
    return this.manifestUrl.indexOf('playlist_index') !== -1;
  }

  createSubtitlePlaylistUrl(url) {
    // live
    if (this.isLive()) {
      return this.createLivePlaylistUrl(url);
    }
    // timeshift
    return this.createTimeShiftPlaylistUrl(url);
  }

  createLivePlaylistUrl(url) {
    if (this.hls.config.subtitleUrl !== undefined) {
      return this.hls.config.subtitleUrl;
    }
    const REGEX = /.+\/tv\/(.+)\/playlist_index\.m3u8/g;
    const result = REGEX.exec(url);
    if (!result) {
        return;
    }
    const channelId = result[1];
    return 'https://demo-cc01.tvapp.jp/apps/subpac/' +channelId +'/caption.m3u8';
  }

  createTimeShiftPlaylistUrl(url) {
    if (this.hls.config.subtitleUrl !== undefined) {
      return this.hls.config.subtitleUrl;
    }
    var REGEX = /.+\/tv\/(.+)\/master_ts_(.+)\.m3u8/g;
    const result = REGEX.exec(url);
    if (!result) {
        return;
    }
    const channelId = result[1];
    const startTime = result[2];
    return 'https://demo-cc01.tvapp.jp/apps/subpac/' +channelId +'/caption_ts_' +startTime +'.m3u8';

  }

  createTextTrack(kind, label, lang) {
    if (this.media) {
      return this.media.addTextTrack(kind, label, lang);
    }
  }
  setupTextTrack(textTrack) {
    this.enableNativeRendering(this.useNativeRendering);
    const self = this;
    const trackDebug = document.getElementById('subtitle-debug-view');
    const trackContainer = document.getElementById('text-track-container');
    textTrack.oncuechange = () => {
      // set cue style
      let activeCues = Array.from(textTrack.activeCues);
      if (activeCues.length > 0) {
        const lastCue = activeCues[activeCues.length-1];
        self.setCueStyle(lastCue, this.useNativeRendering);
        // arib extended. indefinite対応
        Array.from(activeCues)
          .filter(cue => lastCue.startTime !== cue.startTime)
          .map(cue => {
            //textTrack.removeCue(cue);
            cue.endTime = lastCue.startTime;
            return cue;
          });
        activeCues = Array.from(activeCues).filter(cue => lastCue.startTime === cue.startTime);
      }
      if (this.useNativeRendering || trackContainer === undefined) return;
      trackContainer.innerHTML = '';
      let text = '';
      activeCues.forEach((cue, i) => {
        self.showTextTrackCue(cue, trackContainer);
        //console.dir(cue);
        // for debug
        if (trackDebug !== null) {
          const newEl = document.createElement('div');
          if (self.hls.config.debug) {
            let debugText = cue.startTime +' - ' +cue.endTime;
            debugText += '<br/><a class="caption-link" style="font-size:24px;background-color:#999;">' +cue.text +'</a>';
            debugText += '<br/>' +cue.text.replace(/</g, '&lt;').replace(/>/g, '&gt;');
            if(cue.url !== undefined) {
              const path = cue.url.substring(cue.url.lastIndexOf('/')+1);
              debugText += '<br/> <a target="_blank" href="' +cue.url +'">' +path +'</a>';
            }
            newEl.innerHTML = debugText;
            trackDebug.insertBefore(newEl, trackDebug.firstChild);
            newEl.getElementsByClassName('caption-link')[0].onClick = () => {this.media.currentTime = cue.startTime;};
          }
          text += cue.text;
        }
      });
      self.hls.trigger('SUBTITLE_CUE_CHANGE', {text : text});
    };
    return textTrack;
  }

  enableNativeRendering(useNativeRendering) {
    var styleEl = document.createElement('style');
    // for native
    if (useNativeRendering) {
      styleEl.innerText = 'video::cue {visibility: visible;}';/* use html5 native rendering */
      document.head.appendChild(styleEl);
      return;
    }
    // for custom
    styleEl.innerText = 'video::cue {visibility: hidden;}';/* not use html5 native rendering */
    document.head.appendChild(styleEl);
  }

  showTextTrackCue(cue, trackContainer) {
    const cueElement = document.createElement("div");
    if (isWindows()) {
      cueElement.innerHTML = this.getCueAsHTMLForEdge(cue);
    } else {
      cueElement.appendChild(cue.getCueAsHTML());
    }
    trackContainer.appendChild(cueElement); // クラス指定したスタイルを取得するために先にappendしておく必要あり
    // common
    cueElement.style.display = 'inline-block';
    cueElement.style.position = 'absolute';
    cueElement.style.left = cue.position + '%'; // %
    cueElement.style.top = cue.line + '%'; // %
    //cueElement.style.width = '100%';
    cueElement.style.width = 'fit-content';
    // ssz
    this.setSszStyle(cueElement, cue);
    // msz
    this.setMszStyle(cueElement, cue);
    // nsz
    this.setNszStyle(cueElement, cue);
    // drcs
    this.replaceDrcsPlaceHolder(cueElement, cue);
  }

  getCueAsHTMLForEdge(cue) {
    if (cue.text === undefined || cue.text === '') { console.log('empty'); return ''};
    const replace = (all, g1, g2) => {
      const classes = ' class="' + g1.replace(REG_VTT_CLASS_EDGE, ' $1') + '"';
      return '<span' +classes +'>' + g2 +'</span>';
    };
    return cue.text.replace(REG_VTT_TAG_EDGE, replace);
  }

  setCueStyle(cue, useNativeRendering) {
    var cueStyle = document.getElementById('vtt-cue-style');
    var newStyle = false;
    if (!cueStyle) {
      cueStyle = document.createElement('style');
      cueStyle.id = 'vtt-cue-style';
      newStyle = true;
    }
    if (useNativeRendering) {
      cueStyle.innerText = cue.style;
    }
    else {
      cueStyle.innerText = this.vttPseudoToCustomStyle(cue.style);
    }
    if (newStyle) {
      document.head.appendChild(cueStyle);
    }
  }

  vttPseudoToCustomStyle(style) {
    if (style === undefined) return '';
    var customStyle =
      style.replace(/::cue\(([^)]+)\)/g, '$1')
        .replace(/::cue/g, '#text-track-container > div, #text-track-container > div > span');
    return customStyle;
  }

  setSszStyle(cueElement, cue) {
    const sszEls = cueElement.getElementsByClassName('ssz');
    if (sszEls.length === 0) return;
    Array.from(sszEls).forEach((el) => {
      el.style.paddingLeft = cue.letterSpacing/2 + 'em'; // em
    });
  }
  setMszStyle(cueElement, cue) {
    const mszEls = cueElement.getElementsByClassName('msz');
    if (mszEls.length === 0) return;
    const spanStyle = window.getComputedStyle(mszEls[0]);
    cueElement.style.backgroundColor = spanStyle.backgroundColor;
    cueElement.style.whiteSpace = 'pre';
    Array.from(mszEls).forEach((el) => {
      const textLen = Math.max(1, el.innerText.length); // この時点では DRCS の要素がないから
      el.style.marginRight = -((1 + cue.letterSpacing) * textLen)/2 + 'em'; // em
      el.style.backgroundColor = 'transparent';
      //el.style.paddingLeft = cue.letterSpacing/4 + 'em'; // em
    });
  }
  setNszStyle(cueElement, cue) {
    const nszEls = cueElement.getElementsByClassName('nsz');
    if (nszEls.length === 0) return;
    const spanStyle = window.getComputedStyle(nszEls[0]);
    cueElement.style.backgroundColor = spanStyle.backgroundColor;
    Array.from(nszEls).forEach((el) => {
      //el.style.paddingLeft = cue.letterSpacing/2 + 'em'; // em
      el.style.backgroundColor = 'transparent';
    });
  }
  replaceDrcsPlaceHolder(cueElement, cue) {
    //const drcsEls = cueElement.getElementsByClassName('drcs');
    const els = cueElement.children;
    if (els.length === 0) {
      return;
    }
    //const drcsList = cue.drcs;
    var self = this;
    Array.from(els).forEach((el) => {
      const classNames = el.className.split(' ');
      let isOrn = false;
      Array.from(classNames).forEach((className) => {
        isOrn |= className === 'orn-stroke';
      });

      Array.from(classNames).forEach((className) => {
        if (!className.startsWith('drcs-')) return;
        el.innerText = '';
        const drcs = self.getDrcsStyles(className);
        if (!drcs) return;
        const spanStyle = window.getComputedStyle(el);
        const canvas = DrcsDecoder.draw(drcs.width, drcs.height, drcs.depth, drcs.type, drcs.pattern, spanStyle.color, isOrn);
        canvas.style.width = canvas.style.height = drcs.fontSize;
        canvas.style.marginLeft = canvas.style.marginRight = drcs.horizontalMargin;
        canvas.style.verticalAlign = 'text-bottom';
        el.appendChild(canvas);
      });
    });
  }
  getDrcsStyles(className) {
    var styles = getComputedStyle(document.documentElement);
    return {
      width : styles.getPropertyValue('--' +className + '-width'),
      height : styles.getPropertyValue('--' +className + '-height'),
      depth : styles.getPropertyValue('--' +className + '-depth'),
      type : styles.getPropertyValue('--' +className + '-type'),
      pattern : styles.getPropertyValue('--' +className + '-pattern'),
      horizontalMargin : styles.getPropertyValue('--' +className + '-horizontal-margin'),
      fontSize : styles.getPropertyValue('--' +className + '-fontSize')
    };
  }
}
export default AribSubtitleTrackController;



// WEBPACK FOOTER //
// ./src/hlsjs/arib-subtitle-track-controller.js